import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import {
  ASSET_ID_INVALID_CHARS_PATTERN,
  ASSET_ID_PATTERN_ERROR_MESSAGE,
  DateTimeSettings,
  DeviceAsset,
  DEVICE_FIELD_LIMITS,
  formatBytes as sharedFormatBytes,
  GoogleDeviceData,
  TEXT_LIMIT_EXCEEDED_ERROR_MESSAGE,
  Vendors,
} from '@cdw-ae/common-data';
import { ImmediateErrorStateMatcher, MaterialLibraryModule } from '@cdw-ae/common-frontend-materials';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwDividerModule } from '@cdw-ae/common-ui-components-divider';
import {
  CdwDrawerModule,
  closeDrawer,
  DrawerNotificationService,
  openDrawer,
} from '@cdw-ae/common-ui-components-drawer';
import {
  CdwConfirmationDialogComponent,
  type CdwConfirmationDialogData,
  CdwDialogModule,
} from '@cdw-ae/common-ui-components-dialog';
import {
  DateTimeAngularService,
  TimezoneDateTimePipe,
} from '@cdw-ae/date-time-angular-service';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';

import {
  selectCustomerDateTimeSettings,
  selectCustomerId,
} from '../../../../../../+state/user-identity/user-identity-settings.selectors';
import { TailwindColors } from '../../../../../../common/constants/tailwind-colors';
import { DevicesDataService } from '../../../../../../common/services/device/devices-data.service';
import { JobMonitorService } from '../../../../../../common/services/job-monitor/job-monitor.service';

@Component({
  selector: 'device-information',
  standalone: true,
  imports: [
    CommonModule,
    CdwDividerModule,
    CdwButtonModule,
    CdwDialogModule,
    CdwDrawerModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MaterialLibraryModule,
    TimezoneDateTimePipe,
  ],
  templateUrl: './device-information.component.html',
  styleUrl: './device-information.component.scss',
})
export class DeviceInformationComponent implements OnInit, OnDestroy {
  public customButtonColor: string = TailwindColors.tertiaryBlueCta;
  public gray300: string = TailwindColors.gray300;
  public primaryWhite: string = TailwindColors.primaryWhite;
  @Input() deviceId: string | null = null;
  @Input() deviceData: DeviceAsset | null = null;
  @Output() deviceUpdated = new EventEmitter<void>();
  public loading = false;
  public error: string | null = null;
  public editForm!: FormGroup;
  public isSaving = false;
  public isJobRunning = false; // Track if background job is running

  // Step navigation properties
  public currentStep = 1;
  public totalSteps = 2;
  public originalValues: Record<string, string> = {};
  public changedFields: Array<{
    field: string;
    label: string;
    oldValue: string;
    newValue: string;
  }> = [];

  // Date/time settings observable
  dateTimeSettings$!: Observable<DateTimeSettings>;

  // Current date/time settings for internal formatting
  private currentDateTimeSettings!: DateTimeSettings;

  private destroy$ = new Subject<void>();

  // Customer ID for job monitoring
  private customerId = '';

  constructor(
    private devicesDataService: DevicesDataService,
    private fb: FormBuilder,
    private store: Store,
    private snackBar: MatSnackBar,
    private drawerNotifications: DrawerNotificationService,
    private dateTimeService: DateTimeAngularService,
    private jobMonitorService: JobMonitorService,
    private dialog: MatDialog
  ) {
    this.dateTimeSettings$ = this.store.select(selectCustomerDateTimeSettings);
    // Subscribe to settings changes
    this.dateTimeSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.currentDateTimeSettings = settings;
      });
    // Subscribe to customer ID for job monitoring
    this.store
      .select(selectCustomerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((customerId) => {
        this.customerId = customerId;
      });
    this.initializeEditForm();
  }

  ngOnInit(): void {
    // Device data will be provided via @Input from parent component
  }

  // Character limits per EDUS-2051 acceptance criteria (using shared constants)
  readonly FIELD_LIMITS = DEVICE_FIELD_LIMITS;

  // Pattern for Asset ID - disallow < > | \ / characters
  readonly ASSET_ID_PATTERN = ASSET_ID_INVALID_CHARS_PATTERN;

  // Error messages using shared constants
  readonly ASSET_ID_ERROR_MESSAGE = ASSET_ID_PATTERN_ERROR_MESSAGE;
  readonly TEXT_LIMIT_ERROR_MESSAGE = TEXT_LIMIT_EXCEEDED_ERROR_MESSAGE;

  // Immediate error state matcher - shows errors as user types
  errorStateMatcher = new ImmediateErrorStateMatcher();

  /**
   * Check if a field's character count exceeds its limit
   * Used to style counter in red when over limit
   */
  isOverLimit(fieldName: keyof typeof DEVICE_FIELD_LIMITS): boolean {
    const control = this.editForm.get(fieldName);
    const limit = this.FIELD_LIMITS[fieldName];
    return (control?.value?.length || 0) > limit;
  }

  hasInvalidCharsError(): boolean {
    const control = this.editForm.get('annotatedAssetId');
    return !!(control?.hasError('invalidCharacters') && (control.dirty || control.touched));
  }

  /**
   * Custom validator for Asset ID that checks for restricted characters < > | \ /
   */
  private assetIdPatternValidator() {
    return (control: { value: string }) => {
      if (!control.value) {
        return null;
      }
      const hasInvalidChars = this.ASSET_ID_PATTERN.test(control.value);
      return hasInvalidChars ? { invalidCharacters: true } : null;
    };
  }

  initializeEditForm(): void {
    this.editForm = this.fb.group({
      annotatedAssetId: ['', [
        Validators.maxLength(this.FIELD_LIMITS.annotatedAssetId),
        this.assetIdPatternValidator()
      ]],
      annotatedUser: ['', Validators.maxLength(this.FIELD_LIMITS.annotatedUser)],
      annotatedLocation: ['', Validators.maxLength(this.FIELD_LIMITS.annotatedLocation)],
      notes: ['', Validators.maxLength(this.FIELD_LIMITS.notes)],
    });
  }

  editDevice(): void {
    // Reset step navigation
    this.currentStep = 1;
    this.changedFields = [];

    // Store original values
    this.originalValues = {
      annotatedAssetId: String(
        this.deviceData?.vendorProperties?.[Vendors.GOOGLE]?.annotatedAssetId || ''
      ),
      annotatedUser: String(
        this.deviceData?.vendorProperties?.[Vendors.GOOGLE]?.annotatedUser || ''
      ),
      annotatedLocation: String(
        this.deviceData?.vendorProperties?.[Vendors.GOOGLE]?.annotatedLocation || ''
      ),
      notes: String(this.deviceData?.vendorProperties?.[Vendors.GOOGLE]?.notes || ''),
    };

    // Populate form with current values
    this.editForm.patchValue(this.originalValues);

    // Open the drawer using store dispatch
    this.store.dispatch(openDrawer({ id: 'deviceEditDrawer' }));
    this.drawerNotifications.notifyOpenDrawer('deviceEditDrawer');
  }

  /**
   * Attempt to close the drawer
   * Shows confirmation dialog if there are unsaved changes
   */
  attemptCloseDrawer(): void {
    if (this.hasUnsavedChanges()) {
      this.showDiscardChangesDialog();
    } else {
      this.closeEditDrawer();
    }
  }

  /**
   * Check if the form has unsaved changes
   */
  private hasUnsavedChanges(): boolean {
    if (!this.editForm) return false;

    const currentValues = this.editForm.value;
    return Object.keys(this.originalValues).some(
      (key) => (currentValues[key] || '') !== (this.originalValues[key] || '')
    );
  }

  /**
   * Show the "Discard unsaved changes?" confirmation dialog
   */
  private showDiscardChangesDialog(): void {
    const dialogData: CdwConfirmationDialogData = {
      title: 'Unsaved Changes',
      message: '',
      htmlMessage: 'You have unsaved changes to this device. If you continue, those changes will be lost.<br/><br/><strong>Discard unsaved changes?</strong>',
      type: 'warning',
      layout: 'warning-box',
      confirmText: 'Discard Changes',
      cancelText: 'Go Back',
      confirmButtonColor: 'primary',
      showCancelButton: true,
    };

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '610px',
      data: dialogData,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: { confirmed: boolean }) => {
      if (result?.confirmed) {
        this.closeEditDrawer();
      }
      // If not confirmed, do nothing (stay on the form)
    });
  }

  closeEditDrawer(): void {
    this.store.dispatch(closeDrawer({ id: 'deviceEditDrawer' }));
    this.drawerNotifications.notifyCloseDrawer('deviceEditDrawer');
    this.editForm.reset();
    this.currentStep = 1;
    this.changedFields = [];
  }

  nextStep(): void {
    if (this.currentStep === 1) {
      // Calculate changed fields before moving to confirmation step
      this.calculateChangedFields();

      if (this.changedFields.length === 0) {
        this.snackBar.open('No changes detected', 'OK', {
          duration: 3000,
        });
        return;
      }

      this.currentStep = 2;
    }
  }

  previousStep(): void {
    if (this.currentStep === 2) {
      this.currentStep = 1;
    }
  }

  calculateChangedFields(): void {
    this.changedFields = [];

    const fieldMappings = [
      { key: 'annotatedAssetId', label: 'Asset ID' },
      { key: 'annotatedUser', label: 'Annotated User' },
      { key: 'annotatedLocation', label: 'Annotated Location' },
      { key: 'notes', label: 'Notes' },
    ];

    fieldMappings.forEach(({ key, label }) => {
      const oldValue = this.originalValues[key] || '';
      const newValue = this.editForm.value[key] || '';

      if (oldValue !== newValue) {
        this.changedFields.push({
          field: key,
          label,
          oldValue: oldValue || '(empty)',
          newValue: newValue || '(empty)',
        });
      }
    });
  }

  confirmAndSave(): void {
    if (this.editForm.invalid || !this.deviceId) {
      return;
    }

    this.isSaving = true;
    // Trim whitespace from all fields before saving
    const updatedData = {
      annotatedAssetId: this.editForm.value.annotatedAssetId?.trim() ?? '',
      annotatedUser: this.editForm.value.annotatedUser?.trim() ?? '',
      annotatedLocation: this.editForm.value.annotatedLocation?.trim() ?? '',
      notes: this.editForm.value.notes?.trim() ?? '',
    };

    this.devicesDataService
      .updateDeviceInfo(this.deviceId, updatedData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.success) {
            const jobId = response.id;

            // Show success message
            this.isSaving = false;
            this.snackBar.open('Device update job started successfully', 'OK', {
              duration: 3000,
            });
            this.closeEditDrawer();

            // Monitor job completion and reload device data when done
            this.monitorJobAndReloadDevice(jobId);
          } else {
            this.isSaving = false;
            // API returned success: false
            const errorMessage =
              /*result?.errors?.[0]?.error ||*/ 'Unknown error occurred';
            this.snackBar.open(
              `Failed to update device information: ${errorMessage}`,
              'OK',
              {
                duration: 10000,
              }
            );
          }
        },
        error: (error) => {
          this.isSaving = false;
          // Network or other error
          this.snackBar.open(
            `Failed to update device information: ${error.message || 'Unknown error occurred'}`,
            'OK',
            {
              duration: 10000,
            }
          );
        },
      });
  }

  /**
   * Monitor job execution via Firestore and reload device data when complete
   * Used for single device edits from device summary screen
   */
  private monitorJobAndReloadDevice(jobId: number): void {
    const customerId = parseInt(this.customerId, 10);
    this.isJobRunning = true;
    this.jobMonitorService
      .monitorWithCallbacks(
        customerId,
        jobId,
        () => {
          // Success callback
          this.isJobRunning = false;
          this.deviceUpdated.emit();
          this.snackBar.open('Device information updated successfully', 'OK', {
            duration: 3000,
          });
        },
        () => {
          // Error callback
          this.isJobRunning = false;
          this.snackBar.open('Device update job failed', 'OK', {
            duration: 5000,
          });
        }
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  onFormSubmit(event: Event): void {
    event.preventDefault();
    if (this.currentStep === 1) {
      this.nextStep();
    } else if (this.currentStep === 2) {
      this.confirmAndSave();
    }
  }

  // Helper property for typed Google device data
  private get googleDevice(): GoogleDeviceData | undefined {
    return this.deviceData?.vendorProperties?.[Vendors.GOOGLE];
  }

  // Helper methods for extracting data from complex objects
  getCpuModel(): string {
    return this.googleDevice?.model || 'N/A';
  }

  getMemoryUsage(): string {
    // Try telemetry first, then fallback to top-level systemRamTotal
    const totalMemoryStr =
      this.googleDevice?.totalMemoryBytes || this.googleDevice?.systemRamTotal;
    const totalMemory = Number(totalMemoryStr || 0);
    const freeMemory = Number(this.googleDevice?.availableMemoryBytes || 0);

    if (totalMemory > 0 && freeMemory > 0) {
      const usedMemory = totalMemory - freeMemory;
      const usagePercent = ((usedMemory / totalMemory) * 100).toFixed(1);
      return `${usagePercent}%`;
    }

    // If we have total but no free memory data, just show total
    if (totalMemory > 0) {
      return `${this.formatBytes(totalMemory)} total`;
    }

    return 'N/A';
  }

  getDiskUsage(): string {
    // Try storageStatusReport first (modern format)
    const statusTotal = Number(this.googleDevice?.totalBytes || 0);
    const statusAvailable = Number(this.googleDevice?.availableBytes || 0);

    if (statusTotal > 0) {
      const used = statusTotal - statusAvailable;
      const usagePercent = ((used / statusTotal) * 100).toFixed(1);
      return `${usagePercent}%`;
    }

    // Fallback to nested storageInfo structure (legacy format)
    const nestedTotal = Number(this.googleDevice?.totalDiskBytes || 0);
    const nestedAvailable = Number(this.googleDevice?.availableDiskBytes || 0);

    if (nestedTotal > 0) {
      const used = nestedTotal - nestedAvailable;
      const usagePercent = ((used / nestedTotal) * 100).toFixed(1);
      return `${usagePercent}%`;
    }

    return 'N/A';
  }

  getGraphicsAdapter(): string {
    return this.googleDevice?.driverVersion || 'N/A';
  }

  formatBytes(bytes: number | string): string {
    return sharedFormatBytes(bytes) || 'N/A';
  }

  formatBatteryCapacity(capacity: number | string): string {
    const numCapacity = Number(capacity);
    if (!numCapacity || numCapacity <= 0) {
      return 'N/A';
    }
    return `${numCapacity} mAh`;
  }

  getDesignCapacity(): string {
    const designCapacity = this.googleDevice?.designCapacityMah;
    if (designCapacity) {
      return this.formatBatteryCapacity(designCapacity);
    }
    return 'N/A';
  }

  getFullChargeCapacity(): string {
    const fullChargeCapacity = this.googleDevice?.fullChargeCapacityMah;
    if (fullChargeCapacity) {
      return this.formatBatteryCapacity(fullChargeCapacity);
    }
    return 'N/A';
  }

  // Helper methods for accessing telemetry data
  getLastUpdateCheckDT(): string {
    const value =
      typeof this.googleDevice?.lastUpdateCheckTime === 'number'
        ? String(this.googleDevice?.lastUpdateCheckTime)
        : this.googleDevice?.lastUpdateCheckTime;
    if (!value) return 'N/A';

    // Format the date using the date-time service
    try {
      return this.dateTimeService.format(value, this.currentDateTimeSettings);
    } catch {
      return 'N/A';
    }
  }

  getLastUpdateDT(): string | Date {
    // Last OS update is tracked at the top level as lastSync
    return typeof this.googleDevice?.lastSync === 'number'
      ? String(this.googleDevice?.lastSync)
      : this.googleDevice?.lastSync || 'N/A';
  }

  getLastRebootTime(): string {
    const value =
      typeof this.googleDevice?.lastRebootTime === 'number'
        ? String(this.googleDevice?.lastRebootTime)
        : this.googleDevice?.lastRebootTime;
    if (!value) return 'N/A';

    // Format the date using the date-time service
    try {
      return this.dateTimeService.format(value, this.currentDateTimeSettings);
    } catch {
      return 'N/A';
    }
  }

  getBatteryHealth(): string {
    const healthPercentage = this.googleDevice?.batteryHealthPercentage;
    if (healthPercentage !== undefined) {
      return `${healthPercentage}%`;
    }
    return 'N/A';
  }

  // New helper methods for additional telemetry data
  getCpuUtilization(): string {
    return this.googleDevice?.cpuUtilizationPct
      ? `${this.googleDevice?.cpuUtilizationPct}%`
      : 'N/A';
  }

  getCpuTemperature(): string {
    return this.googleDevice?.temperatureCelsius
      ? `${this.googleDevice?.temperatureCelsius}°C`
      : 'N/A';
  }

  getCpuArchitecture(): string {
    return this.googleDevice?.architecture || 'N/A';
  }

  getCpuMaxClockSpeed(): string {
    const maxClockSpeedKhz = this.googleDevice?.maxClockSpeedKhz;
    if (maxClockSpeedKhz) {
      const ghz = (maxClockSpeedKhz / 1000000).toFixed(2);
      return `${ghz} GHz`;
    }
    return 'N/A';
  }

  getStorageType(): string {
    return this.googleDevice?.type || 'N/A';
  }

  getStorageName(): string {
    return this.googleDevice?.storageName || 'N/A';
  }

  getStorageCapacity(): string {
    const capacityBytes =
      this.googleDevice?.capacityBytes || this.googleDevice?.totalDiskBytes;
    if (capacityBytes) {
      return this.formatBytes(capacityBytes);
    }
    return 'N/A';
  }

  getStorageReadSpeed(): string {
    const readBytesPerSecond = this.googleDevice?.readBytesPerSecond;
    if (readBytesPerSecond) {
      const mbps = (Number(readBytesPerSecond) / (1024 * 1024)).toFixed(1);
      return `${mbps} MB/s`;
    }
    return 'N/A';
  }

  getStorageWriteSpeed(): string {
    const writeBytesPerSecond = this.googleDevice?.writeBytesPerSecond;
    if (writeBytesPerSecond) {
      const mbps = (Number(writeBytesPerSecond) / (1024 * 1024)).toFixed(1);
      return `${mbps} MB/s`;
    }
    return 'N/A';
  }

  getUpdateState(): string {
    // The updateState field doesn't exist in the API response
    // Return 'N/A' as this field is not available
    return 'N/A';
  }

  getTargetOsVersion(): string {
    const targetOsVersion = this.googleDevice?.targetOsVersion;
    return targetOsVersion && targetOsVersion !== '' ? targetOsVersion : 'N/A';
  }

  getUpdateCheckTime(): string {
    const updateCheckTime = this.googleDevice?.updateCheckTime;
    if (!updateCheckTime) return 'N/A';

    const date = new Date(updateCheckTime);
    // Check if date is epoch 0 (1970-01-01) which means "no value"
    if (date.getTime() === 0) return 'N/A';

    // Format the date using the date-time service
    try {
      return this.dateTimeService.format(date, this.currentDateTimeSettings);
    } catch {
      return 'N/A';
    }
  }

  getLastKnownIpAddress(): string {
    return (
      this.googleDevice?.lanIpAddress || this.googleDevice?.ipAddress || 'N/A'
    );
  }

  getWifiSignalStrength(): string {
    const signalStrengthDbm = this.googleDevice?.signalStrengthDbm;
    if (signalStrengthDbm !== undefined) {
      return `${signalStrengthDbm} dBm`;
    }
    return 'N/A';
  }

  getGateway(): string {
    return (
      this.googleDevice?.gatewayIpAddress || this.googleDevice?.gateway || 'N/A'
    );
  }

  getDeviceStatus(): string {
    return String(this.googleDevice?.status || 'N/A');
  }

  getAutoUpdateExpiration(): string | Date {
    // Try autoUpdateThrough first (already formatted date string like "2018-03-01")
    if (
      this.googleDevice?.autoUpdateThrough &&
      typeof this.googleDevice.autoUpdateThrough === 'string'
    ) {
      return this.googleDevice.autoUpdateThrough;
    }

    // Fallback to autoUpdateExpiration (millisecond timestamp)
    const autoUpdateExp = this.googleDevice?.autoUpdateExpiration;
    if (!autoUpdateExp) {
      return 'N/A';
    }

    // Try parsing as a number (milliseconds timestamp)
    if (
      typeof autoUpdateExp === 'string' ||
      typeof autoUpdateExp === 'number'
    ) {
      const timestamp =
        typeof autoUpdateExp === 'number'
          ? autoUpdateExp
          : Number.parseInt(autoUpdateExp, 10);

      if (!Number.isNaN(timestamp) && timestamp > 0) {
        // Create date from milliseconds timestamp and convert to ISO string
        return new Date(timestamp).toISOString();
      }
    }

    // If it's already a date string (ISO format), return as-is
    if (typeof autoUpdateExp === 'string') {
      return autoUpdateExp;
    }

    return 'N/A';
  }

  // New helper methods for additional fields
  getOrgUnitPath(): string {
    return String(this.googleDevice?.orgUnitPath || 'N/A');
  }

  getOsVersion(): string {
    const value = this.googleDevice?.osVersion;
    return value && value !== '' ? String(value) : 'N/A';
  }

  getLastSync(): string | Date {
    const lastSync = this.googleDevice?.lastSync;
    if (!lastSync) return 'N/A';
    // lastSync is already an ISO date string, return as-is for the pipe to handle
    return lastSync as string | Date;
  }

  getAnnotatedAssetId(): string {
    const value = this.googleDevice?.annotatedAssetId;
    return value && value !== '' ? String(value) : 'N/A';
  }

  getAnnotatedUser(): string {
    const value = this.googleDevice?.annotatedUser;
    return value && value !== '' ? String(value) : 'N/A';
  }

  getAnnotatedLocation(): string {
    const value = this.googleDevice?.annotatedLocation;
    return value && value !== '' ? String(value) : 'N/A';
  }

  getNotes(): string {
    const value = this.googleDevice?.notes;
    return value && value !== '' ? String(value) : 'N/A';
  }

  getLastStableChromeVersion(): string {
    // Use accessor function that handles both API (latestStableVersion) and SQL (lastStableChromeVersion) field names
    return this.googleDevice?.lastStableChromeVersion || 'N/A';
  }

  getMaximumDomainAllowedChromeVersion(): string {
    // Use accessor function that handles both API (chromeOSVersionMax) and SQL (maximumDomainAllowedChromeVersion) field names
    return this.googleDevice?.maximumDomainAllowedChromeVersion || 'N/A';
  }

  getBootMode(): string {
    const value = this.googleDevice?.bootMode;
    return value && value !== '' ? String(value) : 'N/A';
  }

  getPlatformVersion(): string {
    const value = this.googleDevice?.platformVersion;
    return value && value !== '' ? String(value) : 'N/A';
  }

  getTpmFirmwareVersion(): string {
    return this.googleDevice?.firmwareVersion || 'N/A';
  }

  getBatteryType(): string {
    return this.googleDevice?.batteryType || 'N/A';
  }

  getLastReportedBatteryUsage(): string {
    if (this.googleDevice?.lastReportedBatteryUsage) {
      return this.googleDevice.lastReportedBatteryUsage;
    }

    const powerSource = this.googleDevice?.powerSource;
    const voltage = this.googleDevice?.voltageMillivolt;
    if (powerSource || voltage) {
      return `${powerSource || 'Unknown'} (${voltage ? `${voltage}mV` : 'N/A'})`;
    }

    return 'N/A';
  }

  getDisplays(): string {
    const displays = this.googleDevice?.display;
    if (displays && Array.isArray(displays) && displays.length > 0) {
      return displays
        .map((display) => {
          const resolution = display?.resolution || 'Unknown';
          const refreshRate = display?.refreshRate || 'Unknown';
          return `${resolution}@${refreshRate}Hz`;
        })
        .join(', ');
    }
    return 'N/A';
  }

  getMacAddress(): string {
    // Use accessor function with fallback to top-level field
    const macAddress = this.googleDevice?.macAddress?.wifi;
    return macAddress ? this.formatMacAddress(macAddress) : 'N/A';
  }

  getEthernetMacAddress(): string {
    // Use accessor function with fallback to top-level field
    const macAddress = this.googleDevice?.macAddress?.ethernet;
    return macAddress ? this.formatMacAddress(macAddress) : 'N/A';
  }

  /**
   * Formats a MAC address to a human-readable format with colons
   * Example: "aabbccddeeff" -> "AA:BB:CC:DD:EE:FF"
   */
  private formatMacAddress(mac: string): string {
    // Remove any existing separators (colons, dashes, spaces)
    const cleanMac = mac.replace(/[:\-\s]/g, '');

    // If the MAC address is not 12 characters, return it as-is
    if (cleanMac.length !== 12) {
      return mac;
    }

    // Split into pairs and join with colons, convert to uppercase
    return (
      cleanMac
        .match(/.{1,2}/g)
        ?.join(':')
        .toUpperCase() || mac
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
